import { useEffect, useState } from "react";
import api from "@/lib/api";

const DISCLAIMER = "Final pricing is confirmed by A1 Screening after technician review.";
const PHONE = "239-434-7306";
const PHONE_TEL = "+12394347306";

const HELP_CALL_LINE = (
    <>
        Questions? Call A1 Screening at{" "}
        <a href={`tel:${PHONE_TEL}`} className="font-bold" style={{ color: "var(--color-accent)" }}>
            {PHONE}
        </a>
        .
    </>
);

// Map raw backend status codes onto plain-English customer-facing labels.
const ESTIMATE_STATUS_LABEL = {
    draft: "Being prepared by our team",
    sent: "Sent to you for review",
    accepted: "Accepted",
    declined: "Declined",
};
const INVOICE_STATUS_LABEL = {
    draft: "Being prepared by our team",
    open: "Open · awaiting payment",
    sent: "Sent to you",
    paid: "Paid in full",
    void: "Cancelled",
    uncollectible: "Marked uncollectible — please call us",
};
const PAYMENT_STATUS_LABEL = {
    requires_payment_method: "Awaiting payment",
    requires_action: "Action needed — please call us",
    processing: "Payment processing",
    succeeded: "Paid",
    canceled: "Cancelled",
    failed: "Last payment attempt failed — please call us",
};

function money(v) {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function useCollection(path, key) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(path)
            .then((r) => setItems(r.data[key] || []))
            .finally(() => setLoading(false));
    }, [path, key]);
    return { items, loading };
}

function Empty({ label }) {
    return (
        <p className="a1-card text-sm" style={{ color: "var(--color-text-muted)" }}>
            No {label} yet. When A1 Screening sends you something, it will appear here.
        </p>
    );
}

function Disclaimer() {
    return (
        <div className="a1-card a1-card-feature">
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                <strong className="a1-accent">Disclaimer:</strong> {DISCLAIMER}
            </p>
        </div>
    );
}

function ReviewFallback({ kind }) {
    return (
        <div
            className="a1-card text-sm"
            data-testid={`customer-${kind}-review-fallback`}
            style={{ background: "rgba(202, 138, 4, 0.08)", borderColor: "var(--color-warning)" }}
        >
            <p className="m-0 mb-1 font-semibold" style={{ color: "var(--color-warning)" }}>
                {kind === "payment" ? "Payment status is being reviewed." : `${kind === "invoice" ? "Invoice" : "Estimate"} status is being reviewed.`}
            </p>
            <p className="m-0" style={{ color: "var(--color-text-muted)" }}>
                Please call A1 Screening at{" "}
                <a href={`tel:${PHONE_TEL}`} className="font-bold" style={{ color: "var(--color-accent)" }}>
                    {PHONE}
                </a>{" "}
                if you have questions.
            </p>
        </div>
    );
}

export function CustomerEstimates() {
    const { items, loading } = useCollection("/customer/estimates", "estimates");
    return (
        <div className="space-y-5" data-testid="customer-estimates">
            <h1 className="text-3xl mb-2">Your estimates</h1>
            <Disclaimer />
            {loading ? (
                <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>
            ) : items.length === 0 ? (
                <Empty label="estimates" />
            ) : (
                items.map((e) => {
                    const amount = money(e.amount_quoted);
                    const statusText = ESTIMATE_STATUS_LABEL[e.status] || null;
                    const incomplete = !amount && !statusText;
                    return (
                        <div key={e.lead_id} className="a1-card" data-testid={`customer-estimate-${e.lead_id}`}>
                            <p
                                className="text-[11px] uppercase tracking-[0.18em] font-bold"
                                style={{ color: "var(--color-accent)" }}
                            >
                                {e.service_needed || "Service request"}
                            </p>
                            <p className="text-lg font-semibold mt-1" style={{ color: "var(--color-text)" }}>
                                {amount || "Estimate being prepared"}
                            </p>
                            {statusText ? (
                                <p className="text-sm mt-1" style={{ color: "var(--color-text-muted)" }}>
                                    Status: {statusText}
                                </p>
                            ) : null}
                            {e.sent_at ? (
                                <p className="a1-helper mt-1">Sent {new Date(e.sent_at).toLocaleString()}</p>
                            ) : null}
                            {incomplete ? (
                                <div className="mt-3">
                                    <ReviewFallback kind="estimate" />
                                </div>
                            ) : null}
                        </div>
                    );
                })
            )}
            <p className="a1-helper">{HELP_CALL_LINE}</p>
        </div>
    );
}

function PayNowButton({ leadId }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const pay = async () => {
        setBusy(true);
        setError("");
        try {
            const r = await api.post(`/customer/pay/${leadId}/checkout`, { origin_url: window.location.origin });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't start the payment. Please call us.");
            setBusy(false);
        }
    };
    return (
        <div className="mt-3">
            <button
                type="button"
                onClick={pay}
                disabled={busy}
                className="a1-btn-primary"
                data-testid={`customer-pay-now-${leadId}`}
            >
                {busy ? "Opening secure checkout…" : "Pay now — secure Stripe checkout"}
            </button>
            {error ? (
                <p className="text-sm mt-2" style={{ color: "var(--color-danger)" }} data-testid={`customer-pay-error-${leadId}`}>{error}</p>
            ) : null}
            <p className="a1-helper mt-2">Payment happens on Stripe's secure page — we never see your card details.</p>
        </div>
    );
}

export function CustomerInvoices() {
    const { items, loading } = useCollection("/customer/invoices", "invoices");
    return (
        <div className="space-y-5" data-testid="customer-invoices">
            <h1 className="text-3xl mb-2">Your invoices</h1>
            <Disclaimer />
            {loading ? (
                <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>
            ) : items.length === 0 ? (
                <Empty label="invoices" />
            ) : (
                items.map((inv) => {
                    const amount = money(inv.amount);
                    const statusText = INVOICE_STATUS_LABEL[inv.status] || null;
                    const incomplete = !amount && !statusText;
                    const ref = inv.lead_id ? `#${String(inv.lead_id).slice(-6).toUpperCase()}` : "";
                    return (
                        <div key={inv.lead_id} className="a1-card" data-testid={`customer-invoice-${inv.lead_id}`}>
                            <p
                                className="text-[11px] uppercase tracking-[0.18em] font-bold"
                                style={{ color: "var(--color-accent)" }}
                            >
                                Invoice {ref}
                            </p>
                            <p className="text-lg font-semibold mt-1" style={{ color: "var(--color-text)" }}>
                                {amount || "Invoice being prepared"}
                            </p>
                            {statusText ? (
                                <p className="text-sm mt-1" style={{ color: "var(--color-text-muted)" }}>
                                    Status: {statusText}
                                </p>
                            ) : null}
                            {inv.sent_at ? (
                                <p className="a1-helper mt-1">Sent {new Date(inv.sent_at).toLocaleString()}</p>
                            ) : null}
                            {inv.hosted_invoice_url ? (
                                <p className="mt-2 text-sm">
                                    <a
                                        href={inv.hosted_invoice_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-bold"
                                        style={{ color: "var(--color-accent)" }}
                                        data-testid={`customer-invoice-link-${inv.lead_id}`}
                                    >
                                        Open invoice →
                                    </a>
                                </p>
                            ) : null}
                            {amount && inv.status !== "paid" && inv.status !== "void" ? (
                                <PayNowButton leadId={inv.lead_id} />
                            ) : null}
                            {incomplete ? (
                                <div className="mt-3">
                                    <ReviewFallback kind="invoice" />
                                </div>
                            ) : null}
                        </div>
                    );
                })
            )}
            <p className="a1-helper">{HELP_CALL_LINE}</p>
        </div>
    );
}

export function CustomerPayments() {
    const { items, loading } = useCollection("/customer/payments", "payments");
    return (
        <div className="space-y-5" data-testid="customer-payments">
            <h1 className="text-3xl mb-2">Your payments</h1>
            <Disclaimer />
            {loading ? (
                <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>
            ) : items.length === 0 ? (
                <>
                    <Empty label="payments" />
                    <ReviewFallback kind="payment" />
                </>
            ) : (
                items.map((p) => {
                    const paid = money(p.amount_paid);
                    const balance = money(p.balance_due);
                    const statusText = PAYMENT_STATUS_LABEL[p.status] || null;
                    const incomplete = !paid && !balance && !statusText;
                    const ref = p.lead_id ? `#${String(p.lead_id).slice(-6).toUpperCase()}` : "";
                    return (
                        <div key={p.lead_id} className="a1-card" data-testid={`customer-payment-${p.lead_id}`}>
                            <p
                                className="text-[11px] uppercase tracking-[0.18em] font-bold"
                                style={{ color: "var(--color-accent)" }}
                            >
                                Payment for {ref}
                            </p>
                            {statusText ? (
                                <p className="text-lg font-semibold mt-1" style={{ color: "var(--color-text)" }}>
                                    {statusText}
                                </p>
                            ) : null}
                            <div className="text-sm mt-2" style={{ color: "var(--color-text-muted)" }}>
                                {paid ? <p className="m-0">Amount paid: <span className="font-bold" style={{ color: "var(--color-text)" }}>{paid}</span></p> : null}
                                {balance ? <p className="m-0">Balance due: <span className="font-bold" style={{ color: "var(--color-text)" }}>{balance}</span></p> : null}
                                {!paid && !balance ? <p className="m-0">No payment recorded yet.</p> : null}
                            </div>
                            {p.last_event_at ? (
                                <p className="a1-helper mt-2">Last update {new Date(p.last_event_at).toLocaleString()}</p>
                            ) : null}
                            {incomplete ? (
                                <div className="mt-3">
                                    <ReviewFallback kind="payment" />
                                </div>
                            ) : null}
                        </div>
                    );
                })
            )}
            <p className="a1-helper">{HELP_CALL_LINE}</p>
        </div>
    );
}

export function CustomerProfile() {
    const [profile, setProfile] = useState(null);
    useEffect(() => {
        api.get("/customer/profile").then((r) => setProfile(r.data.profile));
    }, []);
    if (!profile) return <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>;
    return (
        <div className="space-y-5" data-testid="customer-profile">
            <h1 className="text-3xl mb-2">Your profile</h1>
            <Disclaimer />
            <div className="a1-card grid sm:grid-cols-2 gap-3">
                {[
                    ["Name", profile.name],
                    ["Phone", profile.phone],
                    ["Email", profile.email],
                    ["Service address", profile.service_address],
                    ["City", profile.city],
                ].map(([k, v]) => (
                    <div key={k}>
                        <p
                            className="text-[11px] uppercase tracking-[0.18em] font-bold"
                            style={{ color: "var(--color-text-dim)" }}
                        >
                            {k}
                        </p>
                        <p className="text-base font-semibold" style={{ color: "var(--color-text)" }}>{v || "—"}</p>
                    </div>
                ))}
            </div>
            <p className="a1-helper">
                To update your profile, please call A1 Screening at{" "}
                <a href={`tel:${PHONE_TEL}`} className="font-bold" style={{ color: "var(--color-accent)" }}>
                    {PHONE}
                </a>
                .
            </p>
        </div>
    );
}
